import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ImageIcon from "@mui/icons-material/Image";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import heic2any from "heic2any";
import { useSnackbar } from "notistack";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useUploadImages } from "../../../api";

const generateFolderName = ({ projectLine, incidentReport } = {}) => {
    let parentFolder = "";
    let subFolder = "";

    // An incident report is always filed under its damage number at the root of the library,
    // regardless of whether it was opened from a project line. That is where the Dynamics
    // document location for the incident record looks for it.
    if (incidentReport?.damageNumber) {
        parentFolder = incidentReport.damageNumber.toString();
    } else if (projectLine?.project?.address?.name && projectLine?.project?.seqNumber) {
        parentFolder = projectLine?.project?.address?.name;
        subFolder = projectLine?.project?.seqNumber.toString();
    }
    // Fallback if only address name is available
    else if (projectLine?.project?.address?.name) {
        parentFolder = projectLine?.project?.address?.name;
    }

    if (!parentFolder && !subFolder) return null;
    const fullPath = subFolder ? `${parentFolder}/${subFolder}` : parentFolder;

    return {
        parentFolder,
        subFolder,
        fullPath,
    };
};

const generateMetadata = ({ projectLine, incidentReport } = {}) => {
    const metadata = {
        Heimilisfang: "",
        HeimilisfangId: "",
        Tegund: "",
    };
    if (projectLine?.project?.address?.name) {
        metadata.Heimilisfang = projectLine?.project?.address?.name;
    }
    if (projectLine?.project?.address?.id) {
        metadata.HeimilisfangId = projectLine?.project?.address?.id;
    }
    if (projectLine?.project?.serviceType?.name) {
        metadata.Tegund = projectLine?.project?.serviceType?.name;
    }
    if (incidentReport && incidentReport?.id) {
        metadata.Tegund = "Tjón";
    }
    return metadata;
};

const UploadPhotosButton = forwardRef(({ projectLine, incidentReport, asIconButton = true, onUploadComplete, loading, isAdmin = false }, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const hiddenFileInput = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    // Mirrors selectedFiles so uploadImages() can read them without depending on a re-render
    const selectedFilesRef = useRef([]);
    const [fileCount, setFileCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => setIsLoading(loading), [loading]);
    
    const { mutateAsync: uploadPhotosAsync } = useUploadImages();

    const handleUploadComplete = useCallback(
        (success) => {
            enqueueSnackbar(success ? "Images uploaded successfully" : "Failed uploading images", {
                variant: success ? "success" : "error",
                preventDuplicate: true,
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
            });
            if (success && onUploadComplete) onUploadComplete();

            setSelectedFiles([]);
            selectedFilesRef.current = [];
            setFileCount(0);
            setIsLoading(false);
        },
        [onUploadComplete, enqueueSnackbar]
    );

    // Posts the users images to the backend.
    // Both arguments are optional overrides: callers that have just created an incident report
    // pass it in directly, because the `incidentReport` prop has not re-rendered us yet.
    const handleSubmit = useCallback(
        async (filesToUpload, reportOverride) => {
            const files = Array.isArray(filesToUpload) ? filesToUpload : selectedFilesRef.current ?? [];
            if (!files || files.length === 0) return;

            const report = reportOverride ?? incidentReport;
            const folderName = generateFolderName({ projectLine, incidentReport: report });
            const metadata = generateMetadata({ projectLine, incidentReport: report });

            if (!folderName) {
                console.error("Cannot upload images: no target folder could be resolved.", { projectLine, report });
                handleUploadComplete(false);
                return;
            }

            setIsLoading(true);

            const uploadPromises = [];
            for (let image of files) {
                if (!image.data) {
                    enqueueSnackbar("Image is invalid", {
                        variant: "error",
                        preventDuplicate: true,
                        autoHideDuration: 3000,
                        anchorOrigin: { vertical: "bottom", horizontal: "center" },
                    });
                    setIsLoading(false);
                    return;
                }
                const payload = {
                    FolderName: !isAdmin ? folderName.fullPath : folderName.parentFolder,
                    FileName: image.name,
                    Metadata: { ...metadata },
                    Base64: image?.data,
                };
                uploadPromises.push(uploadPhotosAsync(payload));
            }

            try {
                await Promise.all(uploadPromises);
                handleUploadComplete(true);
            } catch (error) {
                console.error("Image upload failed", error);
                handleUploadComplete(false);
            } finally {
                setIsLoading(false);
            }
        },
        [projectLine, incidentReport, isAdmin, uploadPhotosAsync, handleUploadComplete, enqueueSnackbar]
    );

    // Handles file uploads from the user
    const handleFileUpload = async (event) => {
        event.preventDefault();
        const files = event.target.files;
        const validFiles = [];

        for (let i = 0; i < files.length; i++) {
            console.info("Converting file to base64 of type:", files[i].type);
            const base64 = await convertToBase64(files[i]);
            validFiles.push({ name: files[i].name, data: base64 });
        }
        setFileCount(validFiles.length);
        setSelectedFiles(validFiles);
        selectedFilesRef.current = validFiles;

        console.info("📁 Valid files:", validFiles);

        if (asIconButton && validFiles.length > 0) {
            enqueueSnackbar(validFiles.length === 1 ? "Uploading Image..." : `Uploading ${validFiles.length} Images...`, {
                variant: "info",
                preventDuplicate: true,
                autoHideDuration: 3000,
                anchorOrigin: { vertical: "bottom", horizontal: "center" },
            });
            handleSubmit(validFiles);
        }
    };

    // Converts a file to a base64 string for the backend
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            if (file.name.toLowerCase().endsWith(".heic")) {
                // HEIC file handling
                heic2any({
                    blob: file,
                    toType: "image/jpeg",
                    quality: 1,
                })
                    .then((convertedBlob) => {
                        // Convert the converted blob to Base64
                        const reader = new FileReader();
                        reader.readAsDataURL(convertedBlob);
                        reader.onloadend = () => resolve(reader.result);
                        reader.onerror = reject;
                    })
                    .catch(reject);
            } else {
                // Standard file handling
                const fileReader = new FileReader();
                fileReader.readAsDataURL(file);
                fileReader.onload = () => resolve(fileReader.result);
                fileReader.onerror = (error) => reject(error);
            }
        });
    };

    // Exposes the handleSubmit callback function
    useImperativeHandle(ref, () => ({
        uploadImages: handleSubmit,
    }));

    const handleClick = () => hiddenFileInput.current.click();

    return (
        <Box>
            {asIconButton ? (
                <>
                    <input type="file" multiple ref={hiddenFileInput} onChange={handleFileUpload} style={{ display: "none" }} />
                    <IconButton edge="start" color="inherit" aria-label="Upload" onClick={() => hiddenFileInput.current.click()}>
                        <AddIcon fontSize="large" />
                    </IconButton>
                </>
            ) : (
                <>
                    <input type="file" multiple ref={hiddenFileInput} onChange={handleFileUpload} style={{ display: "none" }} />
                    <Button
                        variant={selectedFiles.length ? "outlined" : "contained"}
                        color="primary"
                        fullWidth
                        startIcon={selectedFiles.length > 0 && isLoading ? <CircularProgress size={20} color="inherit" /> : selectedFiles.length > 0 ? <CheckIcon /> : <ImageIcon />}
                        onClick={handleClick}
                        disabled={isLoading}
                    >
                        {selectedFiles.length ? `Images added (${fileCount})` : "Add Images"}
                    </Button>
                </>
            )}
        </Box>
    );
});

export default UploadPhotosButton;
